import { Suspense, useState } from 'react';
import { Outlet } from 'react-router';
import type { Me } from '@/entities/session';
import { ChangePasswordDialog } from '@/features/change-password';
import { LanguageSwitcher } from '@/features/language-switch';
import type { UiLanguage } from '@/shared/lib';
import { Skeleton } from '@/shared/ui';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';

export interface AppShellProps {
  brandName: string;
  /** «Логотип платформы» из `/admin/config` (`brand.logo_url`). */
  logoUrl?: string | null;
  languages: readonly UiLanguage[];
  me: Me;
}

/**
 * Оболочка (README §1): flex, min-width 1280, height 100vh, min-height 760, фон paper-base;
 * сайдбар + шапка + контент (padding 20 24 48, обёртка max-width 1720 по центру).
 * World — плавающий переключатель языка (если языков больше одного).
 */
export function AppShell({ brandName, logoUrl, languages, me }: AppShellProps) {
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  return (
    <div className="relative flex h-screen min-h-760 w-full min-w-1280 overflow-hidden bg-paper-base font-ui text-ink-900">
      <Sidebar
        brandName={brandName}
        logoUrl={logoUrl}
        me={me}
        onChangePassword={() => setChangePasswordOpen(true)}
      />
      <div className="flex min-w-0 flex-1 flex-col bg-paper-base">
        <Topbar nav={me.nav} />
        <main className="flex-1 overflow-y-auto px-24 pt-20 pb-48">
          <div className="mx-auto max-w-content">
            {/* Страница и её неймспейс локалей грузятся лениво — до готовности скелетон контента. */}
            <Suspense fallback={<ContentSkeleton />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
      <LanguageSwitcher languages={languages} />
      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
    </div>
  );
}

function ContentSkeleton() {
  return (
    <div aria-busy="true" className="flex flex-col gap-14">
      <Skeleton className="h-34 w-320" />
      <Skeleton className="h-320 w-full rounded-18" />
    </div>
  );
}
